import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { LIB_CONFIG } from '../../../lib.config';
import { UrlService } from '../../../services/state/url.service';

interface Required {
  basisdienst: 'WEITERBILDUNG' | 'WEITERBILDUNGSANGEBOT' | 'WEITERBILDUNGSSUCHE';
  favoritTitle: string;
  targetId: string;
}

export interface Weiterbildung extends Required {
  basisdienst: 'WEITERBILDUNG';
  detailsBegin: string | undefined;
  detailsEnd: string | undefined;
  detailsLocation: string | undefined;
}

export interface Weiterbildungsangebot extends Required {
  basisdienst: 'WEITERBILDUNGSANGEBOT';
  detailsAnbieter: string | undefined;
}

export interface Weiterbildungssuche extends Required {
  basisdienst: 'WEITERBILDUNGSSUCHE';
  detailsSearchFilter: string | undefined;
  detailsSearchKeywords: string | undefined;
  detailsSearchLocation: string | undefined;
  detailsSearchPostcode: string | undefined;
}

@Component({
  selector: 'now-wbsuche-lib-favorit-toggle',
  templateUrl: './favorite-toggle.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: []
})
export class FavoriteToggleComponent {
  private readonly router = inject(Router);

  private readonly oiamConfig = inject(LIB_CONFIG).oiam!;

  @Input() entity!: Weiterbildung | Weiterbildungsangebot | Weiterbildungssuche;

  get weiterbildung(): Weiterbildung {
    return <Weiterbildung>this.entity;
  }

  get weiterbildungsAngebot(): Weiterbildungsangebot {
    return <Weiterbildungsangebot>this.entity;
  }

  get weiterbildungsSuche(): Weiterbildungssuche {
    return <Weiterbildungssuche>this.entity;
  }

  /**
   * Der TargetSlug ist der hintere Teil der URL, der an die umgebungsspezifische Basis URL des Basisdienstes angehangen wird,
   * um auf den Favoriten zu verlinken.
   * Der TargetSlug beginnt mit einem Slash. Zulässig sind bis zu 1900 der für eine URL zulässigen Zeichen.
   * Beispiel für Basisdienst WEITERBILDUNG: /termin/234021060?seite=0&sw=Arzt&re=BAY&uf=301,302,303,304&angebot=15345444
   * Beispiel für Basisdienst FOERDERUNG: /privatpersonen/foerderungen/suche/nrw-bildungsurlaub
   */
  get targetSlug(): string {
    switch (this.entity.basisdienst) {
      case 'WEITERBILDUNGSSUCHE':
        return this.prepareSucheSlug(this.router.url);
      case 'WEITERBILDUNGSANGEBOT':
        return this.prepareAngebotSlug(this.router.url);
      default:
        return this.router.url;
    }
  }

  /**
   * clientID der UI, die diese Webcomponent nutzt. Muss für now_profil-service freigeschaltet sein.
   */
  get xApiKey(): string {
    return this.oiamConfig.clientId;
  }

  private prepareSucheSlug(slug: string): string {
    return slug
      .split('&') //Parameterpaare in Paare auftrennen
      .map((entity) => entity.split('=')) //Paare in Key/Value trennen
      .filter(([k, v]) => !k.includes(UrlService.SELECTED_ANGEBOT)) //Angebotsparameter entfernen
      .map(([k, v]) => (k.includes(UrlService.AKTUELLE_SEITE) ? [k, '0'] : [k, v])) //Wert des Seitenparameters überschreiben
      .map((tuple) => tuple.join('=')) //Key/Value Paare zusammenführen
      .join('&'); //Parameterpaare zusammenführen
  }

  /**
   * Setzt den Angebotsparameter in der URL auf das gemerkte Angebot,
   * damit der Favorit das Angebot öffnet und nicht nur die Trefferliste.
   */
  private prepareAngebotSlug(slug: string): string {
    const [pfad, parameter] = slug.split('?'); //Pfad und Parameterteil trennen
    const uebrige = (parameter ?? '')
      .split('&') //Parameterpaare auftrennen
      .filter((paar) => paar !== '' && !paar.startsWith(`${UrlService.SELECTED_ANGEBOT}=`)); //alten Angebotsparameter entfernen
    uebrige.push(`${UrlService.SELECTED_ANGEBOT}=${this.entity.targetId}`); //Neuen Angebotsparameter anhängen
    return `${pfad}?${uebrige.join('&')}`; //Pfad und Parameterpaare zusammenführen
  }
}
